
import { FormData } from '../types';
import { fieldCatalog } from './config';
import { formatValue } from './format';

export interface Accent {
  bar: string;
  value: string;
  badge: string;
}

/** Rotating accent palette for the summary metric cards. */
export const METRIC_ACCENTS: Accent[] = [
  { bar: 'bg-blue-500', value: 'text-blue-600', badge: 'bg-blue-50 text-blue-700' },
  { bar: 'bg-emerald-500', value: 'text-emerald-600', badge: 'bg-emerald-50 text-emerald-700' },
  { bar: 'bg-violet-500', value: 'text-violet-600', badge: 'bg-violet-50 text-violet-700' },
  { bar: 'bg-amber-500', value: 'text-amber-600', badge: 'bg-amber-50 text-amber-700' },
  { bar: 'bg-rose-500', value: 'text-rose-600', badge: 'bg-rose-50 text-rose-700' },
];

function val(values: FormData, id: string): string {
  const field = fieldCatalog[id];
  if (!field) return '';
  return formatValue(field.type, values[id]);
}

/** Contextual sub-caption shown beneath a metric's value. */
export function metricCaption(id: string, values: FormData): string {
  switch (id) {
    case 'totalLoanAmount':
      return [val(values, 'loanPurpose'), `${val(values, 'term')}yr ${val(values, 'mortgageType')}`]
        .filter(Boolean)
        .join(' · ');
    case 'baseLoanAmount':
      return `Base of ${val(values, 'totalLoanAmount') || '—'}`;
    case 'interestRate':
      return `${val(values, 'typeOfLoan') || 'Loan'} · ${val(values, 'mortgageType') || '—'}`;
    case 'ltv':
      return `Value ${val(values, 'estimatedAppraisalValue') || '—'}`;
    case 'typeOfLoan':
      return val(values, 'occupancy');
    case 'expectedPrice':
      return `Investor ${val(values, 'investor') || '—'}`;
    case 'fico':
      return `Lead ${val(values, 'leadSource') || '—'}`;
    case 'earliestCloseDate':
      return `Application ${val(values, 'applicationDate') || '—'}`;
    default: {
      const stage = val(values, 'stage');
      return stage ? `Stage ${stage}` : '';
    }
  }
}

/** Whether the loan's rate is currently locked. */
export function isRateLocked(values: FormData): boolean {
  return String(values.rateLockAtReg ?? '').toLowerCase() === 'lock';
}

/**
 * Secondary pill shown beside the primary badge — currently the lock
 * expiration date, surfaced on the interest rate tile when the rate is locked.
 */
export function metricSubBadge(id: string, values: FormData): string {
  if (id !== 'interestRate') return '';
  if (!isRateLocked(values)) return '';
  const expiration = val(values, 'lockExpirationDate');
  return expiration ? `Expires ${expiration}` : '';
}

/** Small status pill shown in the corner of a metric card. */
export function metricBadge(id: string, values: FormData): string {
  switch (id) {
    case 'totalLoanAmount':
      return val(values, 'loanRecordType');
    case 'interestRate':
      if (isRateLocked(values)) return 'Locked';
      return val(values, 'rateLockAtReg') ? 'Floating' : '';
    case 'ltv': {
      const num = Number(String(values.ltv ?? '').replace(/[^0-9.]/g, ''));
      if (Number.isNaN(num) || !num) return '';
      return num > 95 ? 'High LTV' : 'Conforming';
    }
    case 'typeOfLoan':
      return val(values, 'refinancePurpose');
    case 'expectedPrice':
      return 'Lender Credit';
    default:
      return val(values, 'workflowStatus');
  }
}

